import React, { useEffect, Fragment } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRightIcon, ArrowRightIcon } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { useLanguage } from '../components/LanguageContext';
import { trackEvent as track } from '../utils/analytics';
const reveal = {
  initial: {
    opacity: 0,
    y: 30
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-80px'
  },
  transition: {
    duration: 0.7
  }
};
const COMPARISON_ROWS = [{
  typical: 'One vendor per service',
  typicalFr: 'Un fournisseur par service',
  bciti: 'One modular foundation',
  bcitiFr: 'Une fondation modulaire'
}, {
  typical: 'Anonymous residents',
  typicalFr: 'Citoyens anonymes',
  bciti: 'Verified identities',
  bcitiFr: 'Identités vérifiées'
}, {
  typical: 'Generic notices',
  typicalFr: 'Avis génériques',
  bciti: 'Location-targeted communications',
  bcitiFr: 'Communications géociblées'
}, {
  typical: 'Call the desk',
  typicalFr: 'Téléphoner à l’hôtel de ville',
  bciti: 'Self-service answers',
  bcitiFr: 'Réponses en libre-service'
}, {
  typical: 'Siloed data',
  typicalFr: 'Données en silos',
  bciti: 'Unified operational view',
  bcitiFr: 'Vue opérationnelle unifiée'
}];
const FLOW_STEPS = [{
  en: 'Identity',
  fr: 'Identité'
}, {
  en: 'Communications',
  fr: 'Communications'
}, {
  en: 'Services',
  fr: 'Services'
}, {
  en: 'Transactions',
  fr: 'Transactions'
}, {
  en: 'ATNA Intelligence',
  fr: 'Intelligence ATNA'
}];
const MODULES = [{
  name: 'Identity',
  nameFr: 'Identité',
  to: '/core-infrastructure'
}, {
  name: 'Digital Card',
  nameFr: 'Carte numérique',
  to: '/digital-card'
}, {
  name: 'Requests',
  nameFr: 'Requêtes',
  to: '/requests'
}, {
  name: 'Communications',
  nameFr: 'Communications',
  to: '/communications'
}, {
  name: 'ATNA Waste Collection',
  nameFr: 'Collectes ATNA',
  to: '/collect'
}, {
  name: 'ATNA Smart Water',
  nameFr: 'Compteurs d’eau ATNA',
  to: '/smart-water'
}, {
  name: 'Analytics',
  nameFr: 'Analytique',
  to: '/analytics'
}, {
  name: 'ATNA Intelligence',
  nameFr: 'Intelligence ATNA',
  to: '/atna'
}];
const COMPLIANCE = [{
  en: 'Law 25',
  fr: 'Loi 25'
}, {
  en: 'Canadian hosted',
  fr: 'Hébergé au Canada'
}, {
  en: 'Auditable actions',
  fr: 'Actions traçables'
}, {
  en: 'Consent management',
  fr: 'Gestion du consentement'
}];
export function CitiesGovernment() {
  const {
    lang
  } = useLanguage();
  const isFR = lang === 'FR';
  useEffect(() => {
    track('Page Viewed', {
      page: 'Cities & Government'
    });
  }, []);
  return <div className="w-full min-h-screen bg-white" data-id="element-8087" data-mp="3t5yesbypnh4t">
      <Header data-id="element-8088" data-mp="gl2h3y0ntvfv" />
      <main className="pt-[120px]" data-id="element-8089" data-mp="gkj8j7wwgjei">
        {/* SECTION 1: HERO */}
        <section className="pt-24 pb-32 px-6" data-id="element-8090" data-mp="ljvr3ggqn4ba">
          <div className="max-w-7xl mx-auto" data-id="element-8091" data-mp="likr7rqeygpg">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-8092" data-mp="6mmyykjm1wii">
              <motion.div initial={{
              opacity: 0,
              x: -20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              duration: 0.8
            }} data-id="element-8093" data-mp="6opl2thn75hy">
                <div className="font-sans text-xs font-semibold tracking-[0.2em] text-[#4F46E5] uppercase mb-8" data-id="element-8094" data-mp="3k1rofm82h96l">
                  {isFR ? 'SECTEURS · VILLES ET GOUVERNEMENTS' : 'SECTORS · CITIES & GOVERNMENT'}
                </div>
                <h1 className="font-serif text-5xl md:text-6xl text-[#121212] leading-tight mb-8" data-id="element-8095" data-mp="3b2xrfuqtqgsi">
                  {isFR ? <>
                      Les villes n’ont pas besoin de plus de logiciels.
                      <br data-id="element-8096" data-mp="330f5okar1uvh" />
                      Elles ont besoin d’une fondation.
                    </> : <>
                      Cities don't need more software.
                      <br data-id="element-8097" data-mp="2tavkv93h7qxz" />
                      They need a foundation.
                    </>}
                </h1>
                <p className="font-sans text-xl text-[#121212]/70 leading-relaxed mb-10" data-id="element-8098" data-mp="2pabnq5iwknbu">
                  {isFR ? 'Identité vérifiée, communications, services et intelligence — sur une seule fondation. En production municipale depuis 2017.' : 'Verified identity, communications, services and intelligence — on one foundation. In municipal production since 2017.'}
                </p>
                <div className="flex flex-wrap items-center gap-4" data-id="element-8099" data-mp="dgtv7bh98yas">
                  <Link to="/request-demo" onClick={() => track('Demo CTA Clicked', {
                  location: 'Cities Hero'
                })} className="inline-flex items-center gap-2 px-8 py-4 bg-[#4F46E5] text-white text-base font-medium rounded-none hover:bg-[#4F46E5]/90 transition-colors" data-id="element-8100" data-mp="8kmmxc1dzapo">
                    {isFR ? 'Demander une démo' : 'Request a demo'}
                    <ArrowUpRightIcon className="w-4 h-4" strokeWidth={2} data-id="element-8101" data-mp="3u988afmv0t7g" />
                  </Link>
                  <Link to="/brossard" onClick={() => track('Secondary CTA Clicked', {
                  location: 'Cities Hero'
                })} className="inline-flex items-center gap-2 px-8 py-4 border border-[#E4E6EC] text-[#121212] text-base font-medium rounded-none hover:border-[#4F46E5] hover:text-[#4F46E5] transition-colors" data-id="element-8102" data-mp="24cyp6epjggo">
                    {isFR ? 'Explorer Brossard' : 'Explore Brossard'}
                    <ArrowRightIcon className="w-4 h-4" data-id="element-8103" data-mp="su7hxgjohjok" />
                  </Link>
                </div>
              </motion.div>

              <motion.div initial={{
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              duration: 0.8,
              delay: 0.2
            }} className="flex justify-center" aria-hidden="true" data-id="element-8104" data-mp="17q6q0r6sfjtp">
                <img src={isFR ? 'https://cdn.magicpatterns.com/uploads/8Hd4qGcfVFJBSoD1333yXy/ChatGPT_Image_7_juil._2026,_15_26_12.png' : 'https://cdn.magicpatterns.com/uploads/a2ci2PqK9k4hkmFdi2sFKX/ChatGPT_Image_7_juil._2026,_15_24_20.png'} alt={isFR ? 'Bâtiments municipaux reposant sur une fondation vérifiée — identité, communications, services et intelligence' : 'Municipal buildings resting on one verified foundation — identity, communications, services and intelligence'} className="w-full max-w-md h-auto object-contain" data-id="element-8105" data-mp="ougmgzj7ifdp" />
              </motion.div>
            </div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: 0.35
          }} className="mt-20 pt-8 border-t border-[#E4E6EC]" data-id="element-8106" data-mp="2qpw5hn4ykmsy">
              <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-4 font-sans text-sm font-semibold tracking-wide text-[#121212]/55" data-id="element-8107" data-mp="11dc6cug5pg7v">
                <span data-id="element-8108" data-mp="1x0xi7tee2s7m">
                  {isFR ? 'Plus de 400 000 identités vérifiées' : '400,000+ verified identities'}
                </span>
                <span className="hidden sm:inline text-[#E4E6EC]" data-id="element-8109" data-mp="1nyoafbai9cxx">|</span>
                <span data-id="element-8110" data-mp="1j0momilp2zir">{isFR ? 'Plus de 20 municipalités' : '20+ municipalities'}</span>
                <span className="hidden sm:inline text-[#E4E6EC]" data-id="element-8111" data-mp="2fqm99bf4dohp">|</span>
                <span data-id="element-8112" data-mp="2fpubbty1veo9">{isFR ? 'Depuis 2017' : 'Since 2017'}</span>
              </div>
            </motion.div>
          </div>
        </section>

        {/* SECTION 2: CONTRAST */}
        <section className="py-32 px-6 border-t border-[#E4E6EC] bg-[#FAFAFB]" data-id="element-8113" data-mp="26x0sxnnb3z66">
          <div className="max-w-4xl mx-auto" data-id="element-8114" data-mp="2200n9dudcyfl">
            <div className="text-center mb-20" data-id="element-8115" data-mp="21x4o5m6sc3pk">
              <motion.h2 {...reveal} className="font-serif text-4xl md:text-5xl text-[#121212] mb-6 leading-tight" data-id="element-8116" data-mp="2zeuypdpa3gdi">
                {isFR ? <>
                    Des outils déconnectés.
                    <br data-id="element-8117" data-mp="321dwowwxd7nh" />
                    Ou une fondation.
                  </> : <>
                    Disconnected tools.
                    <br data-id="element-8118" data-mp="3bvnoodhyg3xo" />
                    Or a foundation.
                  </>}
              </motion.h2>
              <motion.p {...reveal} transition={{
              duration: 0.7,
              delay: 0.1
            }} className="font-sans text-xl text-[#121212]/60 max-w-2xl mx-auto" data-id="element-8119" data-mp="3fwhb12fr1aog">
                {isFR ? <>
                    La plupart des villes empilent des logiciels.
                    <br data-id="element-8120" data-mp="3gm3d8db6o6d" />
                    BCITI part de l’identité.
                  </> : <>
                    Most cities stack software.
                    <br data-id="element-8121" data-mp="civdvy8kuhjr" />
                    BCITI starts from identity.
                  </>}
              </motion.p>
            </div>

            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.2
          }} className="bg-white border border-[#E4E6EC]" data-id="element-8122" data-mp="2vz3fh77yddi5">
              <div className="grid grid-cols-2 border-b border-[#E4E6EC] bg-[#FAFAFB]" data-id="element-8123" data-mp="35q7to4pt0ubx">
                <div className="p-6 font-sans text-xs font-semibold tracking-widest text-[#121212]/40 uppercase border-r border-[#E4E6EC]" data-id="element-8124" data-mp="39rjabkqpa57z">
                  {isFR ? 'VILLE TYPIQUE' : 'TYPICAL CITY'}
                </div>
                <div className="p-6 font-sans text-xs font-semibold tracking-widest text-[#4F46E5] uppercase" data-id="element-8125" data-mp="3jq8ujk62k1re">
                  BCITI
                </div>
              </div>
              {COMPARISON_ROWS.map((row, i) => <div key={i} className="grid grid-cols-2 border-b border-[#E4E6EC] last:border-0 hover:bg-[#FAFAFB]/50 transition-colors" data-id="element-8126" data-mp="1pczh5haejw3v" data-mpi={i}>
                  <div className="p-6 font-serif text-lg text-[#121212]/50 border-r border-[#E4E6EC]" data-id="element-8127" data-mp="bg6a20drgcv9">
                    {isFR ? row.typicalFr : row.typical}
                  </div>
                  <div className="p-6 font-serif text-lg text-[#121212]" data-id="element-8128" data-mp="2n2yjxmoea39">
                    {isFR ? row.bcitiFr : row.bciti}
                  </div>
                </div>)}
            </motion.div>

            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.3
          }} className="mt-16 text-center" data-id="element-8129" data-mp="ucxyeo85ti66">
              <p className="font-serif text-2xl md:text-3xl text-[#121212] leading-snug" data-id="element-8130" data-mp="lf5gdcb1zwf3">
                {isFR ? <>
                    L’identité n’est pas un système de plus.
                    <br data-id="element-8131" data-mp="360xbb1hcummu" />
                    C’est la fondation.
                  </> : <>
                    Identity is not another system.
                    <br data-id="element-8132" data-mp="32m5hev86ef2r" />
                    It is the foundation.
                  </>}
              </p>
            </motion.div>
          </div>
        </section>

        {/* SECTION 3: COMPOUNDING VALUE */}
        <section className="py-32 px-6 border-t border-[#E4E6EC]" data-id="element-8133" data-mp="2yjr0igo2hpe4">
          <div className="max-w-5xl mx-auto text-center" data-id="element-8134" data-mp="2yhxsawxt2x40">
            <motion.h2 {...reveal} className="font-serif text-4xl md:text-5xl text-[#121212] mb-10 leading-tight" data-id="element-8135" data-mp="2jumhwydd1j5h">
              {isFR ? <>
                  Déployez une fois.
                  <br data-id="element-8136" data-mp="2aw141giu2sb1" />
                  Élargissez sans cesse.
                </> : <>
                  Deploy once.
                  <br data-id="element-8137" data-mp="20vh0263xj9p4" />
                  Keep expanding.
                </>}
            </motion.h2>
            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.1
          }} className="font-sans text-xl text-[#121212]/70 leading-relaxed max-w-2xl mx-auto mb-20 space-y-1" data-id="element-8138" data-mp="25u1x5wnvxd9c">
              {isFR ? <>
                  <p data-id="element-8139" data-mp="1w8sltvr3xmpk">Les villes déploient d’abord l’identité vérifiée.</p>
                  <p data-id="element-8140" data-mp="1r7cyty5dd64j">Puis les communications.</p>
                  <p data-id="element-8141" data-mp="1rar35bxlfpem">Puis les requêtes.</p>
                  <p data-id="element-8142" data-mp="25749bowog4ds">Puis les collectes.</p>
                  <p data-id="element-8143" data-mp="2562x3h3xdbuh">Puis les compteurs d’eau intelligents.</p>
                  <p data-id="element-8144" data-mp="208b74smubzsx">Puis l’intelligence.</p>
                  <p className="text-[#121212] pt-4" data-id="element-8145" data-mp="2078f2hds7rbf">
                    Chaque module s’appuie sur la même fondation.
                  </p>
                </> : <>
                  <p data-id="element-8146" data-mp="2a18kylgw10l7">Cities deploy verified identity first.</p>
                  <p data-id="element-8147" data-mp="2nz6tvbcfw4tb">Then communications.</p>
                  <p data-id="element-8148" data-mp="2jx208clzr86o">Then requests.</p>
                  <p data-id="element-8149" data-mp="2jy3ib11fcuxw">Then waste collection.</p>
                  <p data-id="element-8150" data-mp="2jv8vj2uxuy1b">Then smart water meters.</p>
                  <p data-id="element-8151" data-mp="h9dcccgqba56">Then intelligence.</p>
                  <p className="text-[#121212] pt-4" data-id="element-8152" data-mp="h8kbtt8d5gvh">
                    Every module builds on the same foundation.
                  </p>
                </>}
            </motion.div>

            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.2
          }} className="flex flex-wrap items-center justify-center gap-4 mb-20" data-id="element-8153" data-mp="r2aughht6x39">
              {FLOW_STEPS.map((step, index) => <Fragment key={step.en} data-id="element-8154" data-mp="3cglwc4ivuot" data-mpi={index}>
                  <div className={`px-6 py-4 border ${index === FLOW_STEPS.length - 1 ? 'border-[#4F46E5] bg-[#4F46E5]/5 text-[#4F46E5]' : 'border-[#E4E6EC] bg-white text-[#121212]'} font-sans text-sm font-medium`} data-id="element-8155" data-mp="89gqeilq4pdt">
                    {isFR ? step.fr : step.en}
                  </div>
                  {index < FLOW_STEPS.length - 1 && <ArrowRightIcon className="w-4 h-4 text-[#E4E6EC] hidden md:block" data-id="element-8156" data-mp="3889l7y5qoxa9" />}
                </Fragment>)}
            </motion.div>

            <motion.p {...reveal} transition={{
            duration: 0.7,
            delay: 0.3
          }} className="font-serif text-2xl md:text-3xl text-[#121212] leading-snug" data-id="element-8157" data-mp="349a83q9utu7q">
              {isFR ? <>
                  Pas un coût récurrent.
                  <br data-id="element-8158" data-mp="2kinln9lggg1q" />
                  Un actif cumulatif.
                </> : <>
                  Not a recurring cost.
                  <br data-id="element-8159" data-mp="2gj6f0cualcuz" />A compounding asset.
                </>}
            </motion.p>
          </div>
        </section>

        {/* SECTION 4: MUNICIPAL PROOF */}
        <section className="py-32 px-6 border-t border-[#E4E6EC] bg-[#FAFAFB]" data-id="element-8160" data-mp="ep76fz8k19lz">
          <div className="max-w-6xl mx-auto" data-id="element-8161" data-mp="9s6yfis59ws8">
            <motion.h2 {...reveal} className="font-serif text-4xl md:text-5xl text-[#121212] text-center mb-20" data-id="element-8162" data-mp="svikvpckypzv">
              {isFR ? 'Éprouvé en production municipale.' : 'Proven in municipal production.'}
            </motion.h2>

            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.1
          }} data-id="element-8163" data-mp="otnliqe7mmoq">
              <div className="font-sans text-xs font-semibold tracking-widest text-[#4F46E5] uppercase mb-6" data-id="element-8164" data-mp="1chdj5tv6jpte">
                {isFR ? 'Déploiements BCITI' : 'BCITI deployments'}
              </div>

              <div className="space-y-6" data-id="element-8165" data-mp="12nek53qekm5o">
                <div className="bg-white border border-[#E4E6EC] p-8" data-id="element-8166" data-mp="17jvieqrbggwh">
                  <h3 className="font-serif text-2xl text-[#121212] mb-4" data-id="element-8167" data-mp="17m80d3x2qr9j">
                    Brossard
                  </h3>
                  <div className="flex flex-wrap gap-x-6 gap-y-2 font-sans text-sm text-[#121212]/70" data-id="element-8168" data-mp="3sb5h9zgpk38s">
                    <span data-id="element-8169" data-mp="3kqbnvajik404">
                      {isFR ? '88 266 usagers actifs' : '88,266 active users'}
                    </span>
                    <span className="hidden sm:inline text-[#E4E6EC]" data-id="element-8170" data-mp="3trja2u8r9edu">|</span>
                    <span data-id="element-8171" data-mp="3tpf84huf2pg7">
                      {isFR ? '78 % de couverture de la population' : '78% population coverage'}
                    </span>
                    <span className="hidden sm:inline text-[#E4E6EC]" data-id="element-8172" data-mp="310rhe6qwf7v9">|</span>
                    <span data-id="element-8173" data-mp="2x3dailqoxc6f">{isFR ? 'Depuis 2017' : 'Since 2017'}</span>
                  </div>
                </div>

                <div className="bg-white border border-[#E4E6EC] p-8" data-id="element-8174" data-mp="3av08cq0a6srb">
                  <h3 className="font-serif text-2xl text-[#121212] mb-4" data-id="element-8175" data-mp="361f8stj1hqq0">
                    Casselman
                  </h3>
                  <div className="font-sans text-sm text-[#121212]/70" data-id="element-8176" data-mp="2iajlo9pj3jp3">
                    {isFR ? 'Compteurs d’eau ATNA en production' : 'ATNA Smart Water in production'}
                  </div>
                </div>

                <div className="bg-white border border-[#E4E6EC] p-8" data-id="element-8177" data-mp="1wu2t2dd6j2ww">
                  <h3 className="font-serif text-2xl text-[#121212] mb-4" data-id="element-8178" data-mp="21ruvw2fd49fv">
                    Clarence-Rockland
                  </h3>
                  <div className="flex flex-wrap gap-x-6 gap-y-2 font-sans text-sm text-[#121212]/70" data-id="element-8179" data-mp="26t26084z9kpp">
                    <span data-id="element-8180" data-mp="2bq29qcecubdn">
                      {isFR ? 'Collectes ATNA' : 'ATNA Waste Collection'}
                    </span>
                    <span className="hidden sm:inline text-[#E4E6EC]" data-id="element-8181" data-mp="2fpsy69gesff0">|</span>
                    <span data-id="element-8182" data-mp="2kjnohvqirsn2">
                      {isFR ? 'Premier déploiement d’IA' : 'First AI deployment'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="mt-8 bg-white border border-[#E4E6EC] px-8 py-6 text-center" data-id="element-8183" data-mp="2uiwg4hxwwv69">
                <p className="font-serif text-xl md:text-2xl text-[#121212] leading-snug" data-id="element-8184" data-mp="2zgeg3ityc024">
                  <span className="text-[#121212]/40" data-id="element-8185" data-mp="nue01c86cw62">
                    {isFR ? 'Un réseau de capteurs mesure.' : 'A sensor network measures.'}
                  </span>{' '}
                  {isFR ? 'L’identité vérifiée agit.' : 'Verified identity acts.'}
                </p>
              </div>
            </motion.div>

            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.2
          }} className="mt-12 border-t border-[#E4E6EC] pt-12" data-id="element-8186" data-mp="16iiyc462boxh">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-8187" data-mp="ruebqp3w2t9f">
                <div className="border border-[#E4E6EC] bg-white p-8 flex flex-col" data-id="element-8188" data-mp="ru4nb5yhsu5u">
                  <div className="font-sans text-xs font-semibold tracking-[0.18em] text-[#4F46E5] uppercase mb-6" data-id="element-8189" data-mp="1pbd1m0ubpslv">
                    {isFR ? 'La recherche montre' : 'Research shows'}
                  </div>
                  <ul className="space-y-5 mb-8" data-id="element-8190" data-mp="3b57fub7qff3x">
                    <li className="font-sans text-base text-[#121212]/70 leading-relaxed" data-id="element-8191" data-mp="364barubn9mwx">
                      {isFR ? 'Les citoyens préfèrent les canaux numériques au téléphone ou aux services en personne, dans une proportion de deux contre un.' : 'Residents prefer digital channels over phone or in-person by two to one.'}
                    </li>
                    <li className="font-sans text-base text-[#121212]/70 leading-relaxed" data-id="element-8192" data-mp="3psadqizyvprs">
                      {isFR ? 'Améliorer la satisfaction pourrait réduire les appels de service jusqu’à 20 % pour certains services.' : 'Improving satisfaction could cut service calls by up to 20% for some services.'}
                    </li>
                    <li className="font-sans text-base text-[#121212]/70 leading-relaxed" data-id="element-8193" data-mp="cmjzdu3zt4z8">
                      {isFR ? 'La confiance peut presque doubler — de 37 % à 72 % — à mesure que la qualité du service augmente.' : 'Trust can nearly double — from 37% to 72% — as service quality rises.'}
                    </li>
                    <li className="font-sans text-base text-[#121212]/70 leading-relaxed" data-id="element-8194" data-mp="txp8t3y5b3v5">
                      {isFR ? 'Les citoyens satisfaits sont 9× plus susceptibles de faire confiance à leur gouvernement.' : 'Satisfied residents are 9× more likely to trust their government.'}
                    </li>
                  </ul>
                  <div className="mt-auto border-t border-[#E4E6EC] pt-6 font-sans text-xs text-[#121212]/40 leading-relaxed" data-id="element-8195" data-mp="1cmm3va1da0ef">
                    {isFR ? 'McKinsey & Company, recherche sur le secteur public. Contexte de marché : il ne s’agit pas de résultats de déploiement BCITI.' : 'McKinsey & Company, public-sector research. Market context; not BCITI deployment results.'}
                  </div>
                </div>

                <div className="border border-[#E4E6EC] bg-white p-8 flex flex-col" data-id="element-8196" data-mp="3io7nfpz5j1c3">
                  <div className="font-sans text-xs font-semibold tracking-[0.18em] text-[#4F46E5] uppercase mb-6" data-id="element-8197" data-mp="95isywmz5gl">
                    {isFR ? 'Contexte du marché canadien' : 'Canadian market context'}
                  </div>
                  <p className="font-sans text-base text-[#121212]/70 leading-relaxed mb-8" data-id="element-8198" data-mp="a9pivy8f63x8">
                    {isFR ? 'Selon le ' : 'Per the '}
                    <a href="https://www.mnp.ca/en/clients/public-sector/2025-municipal-report" target="_blank" rel="noopener noreferrer" className="text-[#4F46E5] underline underline-offset-2 hover:text-[#4F46E5]/80 transition-colors" data-id="element-8199" data-mp="j8jbldimqa5i">
                      {isFR ? 'Rapport municipal MNP 2025' : 'MNP 2025 Municipal Report'}
                    </a>
                    {isFR ? ', 23 % des municipalités canadiennes utilisent désormais l’IA et l’IA est une priorité pour 48 % — mais les systèmes hérités en freinent 43 %. L’appétit est réel ; la fondation manque.' : ', 23% of Canadian municipalities now use AI and AI is a priority for 48% — yet legacy systems hold back 43% of them. The appetite is real; the foundation is missing.'}
                  </p>
                  <div className="mt-auto border-t border-[#E4E6EC] pt-6 font-sans text-xs text-[#121212]/40 leading-relaxed" data-id="element-8200" data-mp="horl3nsv5dzc">
                    {isFR ? 'Contexte de marché : il ne s’agit pas de résultats de déploiement BCITI. Source : Rapport municipal MNP 2025.' : 'Market context. Not BCITI deployment results. Source: MNP 2025 Municipal Report.'}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* SECTION 5: WHAT A CITY DEPLOYS */}
        <section className="py-32 px-6 border-t border-[#E4E6EC]" data-id="element-8201" data-mp="18hrnyxnp1qvk">
          <div className="max-w-5xl mx-auto" data-id="element-8202" data-mp="13hcfajecx7jx">
            <motion.h2 {...reveal} className="font-serif text-4xl md:text-5xl text-[#121212] text-center mb-16" data-id="element-8203" data-mp="13k8c24egxq6s">
              {isFR ? 'Ce qu’une ville déploie.' : 'What a city deploys.'}
            </motion.h2>

            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.1
          }} className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12" data-id="element-8204" data-mp="pqgz0m1j3o6b">
              {MODULES.map(mod => <Link key={mod.name} to={mod.to} className="group flex items-center justify-between p-6 border border-[#E4E6EC] bg-white hover:border-[#4F46E5] transition-colors" data-id="element-8205" data-mp="at85pmso0aby">
                  <span className="font-sans text-sm font-medium text-[#121212] group-hover:text-[#4F46E5] transition-colors" data-id="element-8206" data-mp="2ieou35cn12t9">
                    {isFR ? mod.nameFr : mod.name}
                  </span>
                  <ArrowRightIcon className="w-4 h-4 text-[#121212]/20 group-hover:text-[#4F46E5] transition-colors" data-id="element-8207" data-mp="2rgplhll6wun0" />
                </Link>)}
            </motion.div>

            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.2
          }} className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3" data-id="element-8208" data-mp="1ysjystaksvkj">
              {COMPLIANCE.map((item, i, arr) => <Fragment key={item.en} data-id="element-8209" data-mp="2dhpyeidbsnsy" data-mpi={i}>
                  <span className="font-sans text-xs font-semibold tracking-widest text-[#121212]/40 uppercase" data-id="element-8210" data-mp="1g0r6z278tgee">
                    {isFR ? item.fr : item.en}
                  </span>
                  {i < arr.length - 1 && <span className="hidden sm:inline text-[#E4E6EC]" data-id="element-8211" data-mp="1cycyl8ws593d">|</span>}
                </Fragment>)}
            </motion.div>
          </div>
        </section>

        {/* SECTION 6: CLOSING */}
        <section className="py-40 px-6 border-t border-[#E4E6EC] bg-[#FAFAFB]" data-id="element-8212" data-mp="1mofy7ryeplao">
          <div className="max-w-3xl mx-auto text-center" data-id="element-8213" data-mp="1hr63bws90hfg">
            <motion.h2 {...reveal} className="font-serif text-4xl md:text-5xl text-[#121212] mb-8 leading-tight" data-id="element-8214" data-mp="u00p1m1yhx79">
              {isFR ? 'Une fondation municipale bâtie aujourd’hui devient difficile à remplacer.' : 'A municipal foundation built today becomes difficult to replace.'}
            </motion.h2>
            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.1
          }} className="font-sans text-xl text-[#121212]/60 mb-12 leading-relaxed space-y-2" data-id="element-8215" data-mp="z1q1n64tuixg">
              {isFR ? <>
                  <p data-id="element-8216" data-mp="15ufya0d42dn">Neuf ans en production.</p>
                  <p data-id="element-8217" data-mp="16nfapwzrplr">Des centaines de milliers d’identités vérifiées.</p>
                  <p data-id="element-8218" data-mp="3jc0ea9b4isx5">
                    Les villes qui bâtissent sur une fondation ne reviennent pas
                    aux outils déconnectés.
                  </p>
                </> : <>
                  <p data-id="element-8219" data-mp="fw1nvvsqu303">Nine years in production.</p>
                  <p data-id="element-8220" data-mp="jwc4s3i2qdtq">Hundreds of thousands of verified identities.</p>
                  <p data-id="element-8221" data-mp="jvk7mo8tw81c">
                    Cities that build on a foundation do not return to
                    disconnected tools.
                  </p>
                </>}
            </motion.div>
            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.2
          }} className="flex flex-wrap items-center justify-center gap-4" data-id="element-8222" data-mp="b1xk1leryb38">
              <Link to="/request-demo" onClick={() => track('Demo CTA Clicked', {
              location: 'Cities Final'
            })} className="inline-flex items-center gap-2 px-8 py-4 bg-[#4F46E5] text-white text-base font-medium rounded-none hover:bg-[#4F46E5]/90 transition-colors" data-id="element-8223" data-mp="2znr0bljwxvw6">
                {isFR ? 'Demander une démo' : 'Request a demo'}
                <ArrowUpRightIcon className="w-4 h-4" strokeWidth={2} data-id="element-8224" data-mp="3dn8w4kvmm9f1" />
              </Link>
              <Link to="/brossard" onClick={() => track('Secondary CTA Clicked', {
              location: 'Cities Final'
            })} className="inline-flex items-center gap-2 px-8 py-4 border border-[#E4E6EC] text-[#121212] text-base font-medium rounded-none hover:border-[#4F46E5] hover:text-[#4F46E5] transition-colors" data-id="element-8225" data-mp="3dlfop3d6vl5z">
                {isFR ? 'Explorer Brossard' : 'Explore Brossard'}
                <ArrowRightIcon className="w-4 h-4" data-id="element-8226" data-mp="cqy3aplhhfcw" />
              </Link>
            </motion.div>
          </div>
        </section>
      </main>
      <Footer data-id="element-8227" data-mp="hozww4gjcx2m" />
    </div>;
}